// @ts-check

/**
 * Free-text search terms shared by the lobby and the admin dashboard.
 *
 * A typed query is split into words. Single-character words, punctuation, and
 * a short list of common words are left out. Every remaining word must appear
 * in the searched text. A query that leaves no words matches everything.
 *
 * @module shared/search-text
 */

/** Words dropped from a free-text query. Matching is case-insensitive. */
export const SEARCH_STOP_WORDS = new Set([
  'the',
  'at',
  'with',
  'for',
  'in',
  'on',
  'and',
  'of',
  'is'
]);

/** SQL identifiers accepted by {@link searchTermSql}. */
const SQL_COLUMN = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Words worth searching for.
 *
 * Letters and digits stay. Everything else becomes a word break. A word of
 * one character is dropped, as is any entry in {@link SEARCH_STOP_WORDS}.
 *
 * @param {unknown} query
 * @returns {string[]}
 */
export function searchTerms(query) {
  const raw = String(query ?? '').toLowerCase();
  const words = raw.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  return words.filter((word) => word.length > 1 && !SEARCH_STOP_WORDS.has(word));
}

/**
 * True when every remaining search word appears in at least one field.
 * An empty word list matches.
 *
 * @param {unknown[]} fields
 * @param {unknown} query
 * @returns {boolean}
 */
export function matchesSearch(fields, query) {
  const terms = searchTerms(query);
  if (!terms.length) return true;
  const haystacks = fields.map((field) => String(field ?? '').toLowerCase());
  return terms.every((term) => haystacks.some((hay) => hay.includes(term)));
}

/**
 * SQL fragment that requires every remaining search word to match one of the
 * columns. Callers append it with AND. An empty word list returns no fragment.
 *
 * @param {string[]} columns Trusted column names.
 * @param {unknown} query
 * @returns {{ sql: string, params: string[] }}
 */
export function searchTermSql(columns, query) {
  const terms = searchTerms(query);
  const safe = columns.filter((column) => SQL_COLUMN.test(column));
  if (!terms.length || !safe.length) return { sql: '', params: [] };
  /** @type {string[]} */
  const groups = [];
  /** @type {string[]} */
  const params = [];
  for (const term of terms) {
    groups.push('(' + safe.map((column) => `LOWER(${column}) LIKE ?`).join(' OR ') + ')');
    const like = '%' + term + '%';
    for (let i = 0; i < safe.length; i++) params.push(like);
  }
  return { sql: groups.join(' AND '), params };
}
